import Controller from "../controller/Controller";
import { UserActionType } from "../types/UserAction";
import { ErrorResponse } from "../types/response/ErrorResponse";
import { PayPalInitiate } from "../types/response/PayPalInitiate";
import { loadBraintreeSdk } from "./braintreeSdkLoader";
import * as currencies from '../../currencies.json';

export interface PayPalButtonOptions {
    /** Amount in minor units, matching the charge the merchant will create. */
    amount: number;
    currency: string;
    /** Element or selector the PayPal button is rendered into. */
    container: string | HTMLElement;
    /**
     * Renders the button labelled "Donate" instead of "PayPal"
     */
    donate?: boolean;
    onApprove: (result: PayPalTokenizeResult) => void;
    onError?: (error: any) => void;
    onCancel?: () => void;
}

export interface PayPalTokenizeResult {
    nonce: string;
    payerId: string;
    deviceData: string;
}

/**
 * The gateway creates the PayPal order with intent `authorize` and performs the capture
 * itself, so the client must create an authorize-intent order. A capture-intent order is
 * rejected before the PayPal hop. Both call sites below must agree on this value.
 */
const INTENT = "authorize";

export class PayPalHandler {

    constructor(private readonly _controller: Controller) {
    }

    createButton(options: PayPalButtonOptions): Promise<void> {
        return this._initiate(options.amount, options.currency).then(initiate => {
            if (initiate.testMode) {
                return this._renderTestButton(initiate, options);
            }
            return loadBraintreeSdk().then(braintree => this._render(braintree, initiate, options));
        });
    }

    private _initiate(amount: number, currency: string): Promise<PayPalInitiate> {
        return this._controller.action(UserActionType.INITIATE_PAYPAL, { amount, currency })
            .then((response: PayPalInitiate | ErrorResponse) => {
                const error = (response as ErrorResponse).error;
                if (error) {
                    throw new Error((error as any).message || String(error));
                }
                const initiate = response as PayPalInitiate;
                // Only the Braintree path needs a client token; test mode never mints one.
                if (!initiate.testMode && !initiate.clientToken) {
                    throw new Error("PayPal initiate returned no client token");
                }
                return initiate;
            });
    }

    private _renderTestButton(_initiate: PayPalInitiate, options: PayPalButtonOptions): Promise<void> {
        const container = typeof options.container === "string"
            ? document.querySelector(options.container) as HTMLElement
            : options.container;
        if (!container) {
            return Promise.reject(new Error("PayPal test button: container not found"));
        }
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = "PayPal (test)";
        button.style.cssText = "background:#003087;color:#fff;border:none;padding:10px 20px;cursor:pointer;border-radius:4px;font-size:14px;";
        button.addEventListener("click", () => {
            options.onApprove({
                nonce: "fake-paypal-one-time-nonce",
                payerId: "FAKE-PAYER-ID",
                deviceData: ""
            });
        });
        const note = document.createElement("p");
        note.textContent = "Test mode: this account has no PayPal processor configured, so this "
            + "placeholder replaces the real PayPal button. Clicking it approves the payment "
            + "locally — no request is sent to PayPal. To see the real PayPal button, log out "
            + "and open this page anonymously.";
        note.style.cssText = "margin:8px 0 0;color:#6c757d;font-size:12px;line-height:1.4;";

        container.appendChild(button);
        container.appendChild(note);
        return Promise.resolve();
    }

    private _render(braintree: any, initiate: PayPalInitiate, options: PayPalButtonOptions): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            braintree.client.create({ authorization: initiate.clientToken }, (clientErr, clientInstance) => {
                if (clientErr) {
                    reject(clientErr);
                    return;
                }

                // Braintree exposes deviceData as an already-compact JSON string. It must be
                // forwarded verbatim - re-serializing it produces a pretty-printed blob that
                // the gateway rejects as a format error.
                braintree.dataCollector.create({ client: clientInstance }, (dataErr, dataCollectorInstance) => {
                    if (dataErr) {
                        reject(dataErr);
                        return;
                    }
                    const deviceData = dataCollectorInstance.deviceData;

                    braintree.paypalCheckout.create({ client: clientInstance }, (payPalErr, payPalCheckoutInstance) => {
                        if (payPalErr) {
                            reject(payPalErr);
                            return;
                        }

                        // Braintree passes an error as the first callback argument. Ignoring it
                        // would resolve the promise and then throw from _renderButton outside the
                        // promise chain, leaving the merchant with no button and no error.
                        payPalCheckoutInstance.loadPayPalSDK({
                            currency: options.currency,
                            intent: INTENT
                        }, (sdkErr) => {
                            if (sdkErr) {
                                reject(sdkErr);
                                return;
                            }
                            try {
                                this._renderButton(payPalCheckoutInstance, deviceData, options);
                            } catch (renderErr) {
                                reject(renderErr);
                                return;
                            }
                            resolve();
                        });
                    });
                });
            });
        });
    }

    private _renderButton(payPalCheckoutInstance: any, deviceData: string, options: PayPalButtonOptions) {
        const paypal = (window as any).paypal;

        const rendered = paypal.Buttons({
            fundingSource: paypal.FUNDING.PAYPAL,
            ...(options.donate ? { style: { label: "donate" } } : {}),

            createOrder: () => payPalCheckoutInstance.createPayment({
                flow: "checkout",
                amount: this._toMajorUnits(options.amount, options.currency),
                currency: options.currency,
                intent: INTENT
            }),

            onApprove: (data) => payPalCheckoutInstance.tokenizePayment(data, (tokenizeErr, payload) => {
                if (tokenizeErr) {
                    this._fail(options, tokenizeErr);
                    return;
                }
                options.onApprove({
                    nonce: payload.nonce,
                    payerId: payload.details.payerId,
                    deviceData
                });
            }),

            onCancel: () => {
                if (options.onCancel) {
                    options.onCancel();
                }
            },

            onError: (err) => this._fail(options, err)
        }).render(options.container);

        // render() rejects for a bad container or an ineligible funding source. Without this the
        // rejection is unhandled and never reaches the merchant. Guarded because older SDK builds
        // return undefined rather than a promise.
        if (rendered && typeof rendered.catch === "function") {
            rendered.catch((renderErr) => this._fail(options, renderErr));
        }
    }

    /** Braintree binds the nonce to the order amount, which it expects in major units. */
    private _toMajorUnits(amount: number, currency: string): string {
        const decimals = currencies?.[currency]?.decimalDigits ?? 2;
        return (amount / Math.pow(10, decimals)).toFixed(decimals);
    }

    private _fail(options: PayPalButtonOptions, error: any) {
        if (options.onError) {
            options.onError(error);
        } else {
            console.error("PayPal error", error);
        }
    }
}
